import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Music, User, ListMusic, CheckCircle } from 'lucide-react';
import { resolveTheme } from '../constants/themes';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatGigDate(dateStr, timeStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const formatted = date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  if (!timeStr) return formatted;
  const [h, m] = timeStr.split(':');
  const t = new Date();
  t.setHours(parseInt(h, 10), parseInt(m, 10));
  const time = t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${formatted} · ${time}`;
}

function Home() {
  const [activeGig, setActiveGig] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/gigs/active`)
      .then(res => { setActiveGig(res.data); setLoading(false); })
      .catch(() => setLoading(false));
    axios.get(`${API_URL}/api/settings`).then(res => setBranding(res.data)).catch(() => {});
  }, []);

  const theme = resolveTheme(branding || {});
  const gigDate = activeGig ? formatGigDate(activeGig.gig_date, activeGig.gig_time) : null;

  const pageBg = theme.isDark
    ? `linear-gradient(160deg, ${theme.background} 0%, ${theme.surface} 100%)`
    : theme.surfaceAlt;

  const cardBg = theme.isDark
    ? 'rgba(255,255,255,0.06)'
    : theme.surface;

  const cardBorder = theme.isDark
    ? 'rgba(255,255,255,0.1)'
    : theme.border;

  const iconBoxBg = theme.isDark
    ? `${theme.primary}30`
    : `${theme.primary}20`;

  return (
    <div style={{
      minHeight: '100vh',
      background: pageBg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1.5rem',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{ maxWidth: '480px', width: '100%' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          {!branding?.logo_url && <div style={{ marginBottom: '0.5rem' }}><Music size={48} color={theme.primary} /></div>}
          {loading ? (
            <h1 style={{ color: theme.text, fontSize: '2rem', margin: 0 }}>Loading...</h1>
          ) : activeGig ? (
            <>
              {branding?.logo_url && (
                <img src={branding.logo_url} alt="Band logo" style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '12px', marginBottom: '0.75rem' }} />
              )}
              <h1 style={{ color: theme.text, fontSize: '2.2rem', fontWeight: '700', margin: '0 0 0.25rem', lineHeight: 1.2 }}>
                {branding?.band_name || activeGig.gig_name}
              </h1>
              {branding?.tagline && (
                <p style={{ color: theme.textSecondary, margin: '0 0 0.5rem', fontSize: '1rem', fontStyle: 'italic' }}>
                  {branding.tagline}
                </p>
              )}
              {(activeGig.venue_name || gigDate) && (
                <p style={{ color: theme.textSecondary, margin: '0 0 1rem', fontSize: '0.95rem' }}>
                  {[activeGig.venue_name, gigDate].filter(Boolean).join('  ·  ')}
                </p>
              )}
              <span style={{
                display: 'inline-block',
                background: activeGig.is_accepting_requests ? 'rgba(52,211,153,0.15)' : 'rgba(239,68,68,0.15)',
                border: activeGig.is_accepting_requests ? '1px solid rgba(52,211,153,0.4)' : '1px solid rgba(239,68,68,0.4)',
                color: activeGig.is_accepting_requests ? '#34d399' : '#f87171',
                borderRadius: '999px',
                padding: '0.25rem 0.9rem',
                fontSize: '0.85rem',
                fontWeight: '600',
              }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: activeGig.is_accepting_requests ? '#34d399' : '#f87171', marginRight: '0.4rem', verticalAlign: 'middle' }} />
                {activeGig.is_accepting_requests ? 'Requests are open' : 'Requests not open yet'}
              </span>
            </>
          ) : (
            <>
              <h1 style={{ color: theme.text, fontSize: '2.2rem', fontWeight: '700', margin: '0 0 0.5rem' }}>
                {branding?.band_name || 'GigRequest'}
              </h1>
              {branding?.tagline && (
                <p style={{ color: theme.textSecondary, margin: '0 0 0.5rem', fontSize: '1rem', fontStyle: 'italic' }}>
                  {branding.tagline}
                </p>
              )}
              <p style={{ color: theme.textMuted, margin: 0, fontSize: '1rem' }}>
                No gig is active right now — check back soon.
              </p>
            </>
          )}
        </div>

        {/* How it works */}
        <div style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '16px',
          padding: '1.75rem',
          marginBottom: '1.75rem',
        }}>
          <p style={{
            color: theme.textMuted,
            fontSize: '0.75rem',
            fontWeight: '700',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            margin: '0 0 1.25rem',
            textAlign: 'center',
          }}>
            How it works
          </p>
          {[
            [User, 'Enter your name', 'So we know who to shout out!'],
            [ListMusic, 'Browse the song list', 'See everything we can play tonight'],
            [CheckCircle, 'Tap a song to request it', "We'll do our best to play it!"],
          ].map(([StepIcon, title, sub], i) => (
            <div key={i} style={{ display: 'flex', gap: '1rem', marginBottom: i < 2 ? '1rem' : 0 }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                background: iconBoxBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <StepIcon size={18} color={theme.primary} />
              </div>
              <div>
                <div style={{ color: theme.text, fontWeight: '600', fontSize: '0.95rem' }}>{title}</div>
                <div style={{ color: theme.textMuted, fontSize: '0.82rem', marginTop: '0.1rem' }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        {!loading && (
          activeGig ? (
            <a
              href={`/gig/${branding?.band_slug || activeGig.unique_url}?ref=home`}
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                padding: '1.1rem',
                background: theme.navGradient,
                color: theme.onPrimary,
                textDecoration: 'none',
                borderRadius: '12px',
                fontWeight: '700',
                fontSize: '1.1rem',
                textAlign: 'center',
                boxShadow: `0 4px 24px ${theme.primary}66`,
              }}
            >
              View Songs &amp; Make a Request →
            </a>
          ) : (
            <div style={{
              padding: '1.1rem',
              background: cardBg,
              color: theme.textMuted,
              border: `1px solid ${cardBorder}`,
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '1rem',
              textAlign: 'center',
            }}>
              No active gig at the moment
            </div>
          )
        )}

      </div>
    </div>
  );
}

export default Home;
